import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { GenericSchema, InferOutput } from "valibot";
import { array, boolean, fallback, nullish, number, object, objectAsync, pipe, string, transform } from "valibot";
import { currency, dataName, dataSource, decimalValue, label, required } from "../../utils/validation/base";
import { MAX_VAT } from "../../constants";
import { useTranslation } from "../../state/i18n";
import DataSourceInput from "../DataSourceInput";
import { NumberInput } from "../../components/inputs/NumberInput";
import { dataSourceAsync } from "../../utils/validation/async";
import { createMemo, For, Show } from "solid-js";
import ButtonLink from "../../components/link/ButtonLink";
import DetailModal from "../../components/modal/DetailModal";
import SimpleTable from "../../components/table/SimpleTable";
import FormatCurrency from "../../components/formatting/FormatCurrency";
import { useFormServices } from "../../services/formServices";
import { TextInput } from "../../components/inputs/TextInput";
import { createOptionsForCurrencies } from "../../utils/selectOptionsUtil";
import { SingleSelectInput } from "../../components/menu/Select";
import { isFilledInFilter } from "../../pages/customer/form/filter";
import { useModalManager } from "../../components/modal/ModalManager";
import { FieldButtonPreview, FieldIconButtonPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import type FormId from "../../state/FormId";
import { keyFromSelector } from "i18next";

const ResourceSchema = (formId: FormId) =>
  objectAsync({
    data_source_configuration: dataSourceAsync(formId),
  });

const Schema = object({
  label_text: label(),
  data_name: dataName(),
  data_source_configuration: dataSource(),
  currency: currency(),
  vat_rate: decimalValue(0, MAX_VAT),
  show_prices: fallback(boolean(), true),
  show_vat: fallback(boolean(), true),
  allow_remarks: fallback(boolean(), true),
  use_barcode_scanner: fallback(boolean(), false),
  remember_search: fallback(boolean(), false),
  required: required(),
}) satisfies GenericSchema<InferOutput<ReturnType<typeof ResourceSchema>>>;
const ValueSchema = object({
  priceExVat: pipe(string(), transform(Number.parseFloat)),
  priceIncVat: pipe(string(), transform(Number.parseFloat)),
  vat: pipe(string(), transform(Number.parseFloat)),
  items: array(
    object({
      data: object({
        id: string(),
        name: string(),
        description: string(),
        priceExVat: pipe(string(), transform(Number.parseFloat)),
        vatRate: nullish(string()),
        photo: nullish(string()),
        thumbnail: nullish(string()),
      }),
      quantity: number(),
      vatRate: number(),
      priceExVat: pipe(string(), transform(Number.parseFloat)),
      priceIncVat: pipe(string(), transform(Number.parseFloat)),
      vat: pipe(string(), transform(Number.parseFloat)),
      notes: nullish(string()),
    }),
  ),
});

export type CatalogueFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const CatalogueField: Field<CatalogueFieldProperties, DefaultDtoType, ValueType> = {
  type: "catalogue",
  category: "searchItems",
  widget: "com.moreapps.plugins:catalogue:8",
  schema: Schema,
  resourceSchema: ResourceSchema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    data_source_configuration: undefined,
    currency: "EUR",
    vat_rate: 21,
    show_prices: true,
    show_vat: true,
    allow_remarks: true,
    use_barcode_scanner: false,
    remember_search: true,
    required: false,
  }),
  ruleBehaviour: { conditions: { keys: ["hasValue"], variant: "none" }, actions: { variant: "none" } },
  filter: {
    type: "none",
    definition: [isFilledInFilter],
  },
  renderValue: (value, { fieldConfig }) => {
    const { t } = useTranslation();
    const { open } = useModalManager();
    const hasVAT = value.vat > 0;

    const openModal = () =>
      open({
        content: () => (
          <DetailModal
            title={fieldConfig.properties.label_text}
            iconTile={{ icon: CatalogueField.icon, category: CatalogueField.category }}
            content={() => (
              <SimpleTable
                data={[
                  {
                    label: t(($) => $.CATALOGUE_FIELD_VALUE_ITEMS),
                    value: (
                      <ol>
                        <For each={value.items}>
                          {(item) => (
                            <li>
                              <ButtonLink
                                label={`${item.data.id}. ${item.data.name}, ${item.quantity}`}
                                onClick={() => {
                                  open({
                                    content: () => (
                                      <DetailModal
                                        title={fieldConfig.properties.label_text}
                                        iconTile={{ icon: CatalogueField.icon, category: CatalogueField.category }}
                                        content={() => (
                                          <SimpleTable
                                            data={[
                                              { label: t(($) => $.CATALOGUE_FIELD_VALUE_ITEM_ID), value: item.data.id },
                                              {
                                                label: t(($) => $.CATALOGUE_FIELD_VALUE_ITEM_NAME),
                                                value: item.data.name,
                                              },
                                              {
                                                label: t(($) => $.CATALOGUE_FIELD_VALUE_ITEM_DESCRIPTION),
                                                value: item.data.description,
                                              },
                                              {
                                                label: t(($) => $.CATALOGUE_FIELD_VALUE_ITEM_QUANTITY),
                                                value: item.quantity,
                                              },
                                              {
                                                label: t(($) => $.CATALOGUE_FIELD_VALUE_ITEM_REMARKS),
                                                value: item.notes,
                                              },
                                              {
                                                label: t(($) => $.CATALOGUE_FIELD_VALUE_ITEM_PRICE_PER_ITEM_EX_VAT),
                                                value: (
                                                  <FormatCurrency
                                                    value={item.data.priceExVat}
                                                    currency={fieldConfig.properties.currency}
                                                  />
                                                ),
                                              },
                                              {
                                                label: t(($) => $.CATALOGUE_FIELD_VALUE_ITEM_TOTAL_PRICE_EX_VAT),
                                                value: (
                                                  <FormatCurrency
                                                    value={item.priceExVat}
                                                    currency={fieldConfig.properties.currency}
                                                  />
                                                ),
                                              },
                                              {
                                                label: t(($) => $.CATALOGUE_FIELD_VALUE_ITEM_VAT_RATE),
                                                value: `${item.data.vatRate}%`,
                                              },
                                              {
                                                label: t(($) => $.CATALOGUE_FIELD_VALUE_ITEM_VAT),
                                                value: (
                                                  <FormatCurrency
                                                    value={item.vat}
                                                    currency={fieldConfig.properties.currency}
                                                  />
                                                ),
                                              },
                                              {
                                                label: t(($) => $.CATALOGUE_FIELD_VALUE_ITEM_TOTAL_PRICE_INC_VAT),
                                                value: (
                                                  <FormatCurrency
                                                    value={item.priceIncVat}
                                                    currency={fieldConfig.properties.currency}
                                                  />
                                                ),
                                              },
                                            ]}
                                          />
                                        )}
                                      />
                                    ),
                                  });
                                }}
                              />
                            </li>
                          )}
                        </For>
                      </ol>
                    ),
                  },
                  {
                    label: t(($) => $.CATALOGUE_FIELD_VALUE_PRICE_EX_VAT),
                    value: <FormatCurrency value={value.priceExVat} currency={fieldConfig.properties.currency} />,
                  },
                  {
                    label: t(($) => $.CATALOGUE_FIELD_VALUE_VAT),
                    value: <FormatCurrency value={value.vat} currency={fieldConfig.properties.currency} />,
                  },
                  {
                    label: t(($) => $.CATALOGUE_FIELD_VALUE_PRICE_INC_VAT),
                    value: <FormatCurrency value={value.priceIncVat} currency={fieldConfig.properties.currency} />,
                  },
                ]}
              />
            )}
          />
        ),
      });

    return (
      <ButtonLink
        label={
          <>
            <FormatCurrency value={value.priceIncVat} currency={fieldConfig.properties.currency} />
            {hasVAT ? ` ${t(($) => $.CATALOGUE_FIELD_VALUE_LABEL_AFFIX)}` : ""}
          </>
        }
        onClick={openModal}
      />
    );
  },
  icon: "shoppingCart",
  titleKey: keyFromSelector(($) => $.CATALOGUE_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.CATALOGUE_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    data_source_configuration: keyFromSelector(($) => $.FIELD_PROPERTY_DATASOURCE),
    currency: keyFromSelector(($) => $.FIELD_PROPERTY_CURRENCY),
    vat_rate: keyFromSelector(($) => $.FIELD_PROPERTY_VAT_RATE),
    show_prices: keyFromSelector(($) => $.FIELD_PROPERTY_SHOW_PRICES),
    show_vat: keyFromSelector(($) => $.FIELD_PROPERTY_SHOW_VAT),
    allow_remarks: keyFromSelector(($) => $.FIELD_PROPERTY_ALLOW_REMARKS),
    use_barcode_scanner: keyFromSelector(($) => $.FIELD_PROPERTY_USE_BARCODE_SCANNER),
    remember_search: keyFromSelector(($) => $.FIELD_PROPERTY_REMEMBER_SEARCH),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
  },
  PreviewContent: (props) => {
    const { t } = useTranslation();
    return (
      <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
        <div class="flex w-full gap-x-1">
          <FieldButtonPreview icon="plus" text={t(($) => $.CATALOGUE_FIELD_PREVIEW_ADD_BUTTON_LABEL)} />
          <Show when={props.properties.use_barcode_scanner}>
            <FieldIconButtonPreview icon="qrcode" size="md" />
          </Show>
        </div>
      </PreviewContentLabel>
    );
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();
    const currencyOptions = createMemo(() => createOptionsForCurrencies(t));

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_DATA)}>
          <DataSourceInput
            label={t(props.propertyKeys.data_source_configuration)}
            value={props.data.properties.data_source_configuration}
            required
            errorMessage={props.getError("data_source_configuration")}
            onSelect={(value) => props.setValue("data_source_configuration", value)}
            sameWidth
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_DISPLAY)}>
          <Toggle
            block
            label={t(props.propertyKeys.show_prices)}
            checked={props.data.properties.show_prices}
            setChecked={(value) => props.setValue("show_prices", value)}
            description={t(($) => $.FIELD_PROPERTY_SHOW_PRICES_DESCRIPTION)}
          />
          <Toggle
            block
            label={t(props.propertyKeys.show_vat)}
            checked={props.data.properties.show_vat}
            setChecked={(value) => props.setValue("show_vat", value)}
            description={t(($) => $.FIELD_PROPERTY_SHOW_VAT_DESCRIPTION)}
          />
          <Toggle
            block
            label={t(props.propertyKeys.allow_remarks)}
            checked={props.data.properties.allow_remarks}
            setChecked={(value) => props.setValue("allow_remarks", value)}
            description={t(($) => $.FIELD_PROPERTY_ALLOW_REMARKS_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_PRICING)}>
          <SingleSelectInput
            label={t(props.propertyKeys.currency)}
            value={props.data.properties.currency}
            errorMessage={props.getError("currency")}
            options={currencyOptions()}
            onSelect={(value) => props.setValue("currency", value)}
            sameWidth
          />
          <NumberInput
            label={t(props.propertyKeys.vat_rate)}
            value={props.data.properties.vat_rate}
            minValue={0}
            maxValue={MAX_VAT}
            allowDecimal
            required
            onBlur={(value) => props.setValue("vat_rate", value)}
            errorMessage={props.getError("vat_rate")}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(props.propertyKeys.remember_search)}
            checked={props.data.properties.remember_search}
            setChecked={(value) => props.setValue("remember_search", value)}
            description={t(($) => $.FIELD_PROPERTY_REMEMBER_SEARCH_DESCRIPTION)}
          />
          <Toggle
            block
            label={t(props.propertyKeys.use_barcode_scanner)}
            checked={props.data.properties.use_barcode_scanner}
            setChecked={(value) => props.setValue("use_barcode_scanner", value)}
            description={t(($) => $.FIELD_PROPERTY_USE_BARCODE_SCANNER_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(CatalogueField);
